import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";

type Props = {
  open: boolean;
  currentToken?: string | number | null;
  patientName?: string;
  saving?: boolean;
  onCancel: () => void;
  onConfirm: (token: number) => void;
};

export default function UpdateTokenModal({
  open,
  currentToken,
  patientName,
  saving = false,
  onCancel,
  onConfirm,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const initial =
      currentToken != null && currentToken !== ""
        ? String(currentToken)
        : "";
    setValue(initial);
    setError("");
    const t = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 50);
    return () => window.clearTimeout(t);
  }, [open, currentToken]);

  if (!open) return null;

  const submit = () => {
    const n = parseInt(String(value).trim(), 10);
    if (!Number.isFinite(n) || n < 0) {
      setError("Enter a valid token number (0 or greater).");
      return;
    }
    setError("");
    onConfirm(n);
  };

  const body = (
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-token-title"
      onClick={saving ? undefined : onCancel}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
          <div>
            <h2
              id="update-token-title"
              className="text-base font-semibold text-slate-900 dark:text-slate-100"
            >
              Update token
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {patientName
                ? `Enter the new token number for ${patientName}.`
                : "Enter the new token number for this appointment."}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="px-5 pb-2">
          <label
            htmlFor="update-token-input"
            className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5"
          >
            Token number
          </label>
          <input
            ref={inputRef}
            id="update-token-input"
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            value={value}
            disabled={saving}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
              if (e.key === "Escape" && !saving) onCancel();
            }}
            className="w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3.5 py-2.5 text-base font-semibold tabular-nums text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary disabled:opacity-60"
            placeholder="e.g. 12"
          />
          {error ? (
            <p className="mt-1.5 text-xs text-rose-600 dark:text-rose-400">
              {error}
            </p>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100 dark:border-slate-700">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="px-3.5 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={submit}
            className="px-3.5 py-2 rounded-lg text-sm font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? "Updating…" : "Update token"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(body, document.body);
}
